import React, { useEffect, useRef, useState } from "react";
// Il lettore di codici del browser, dove manca. Chrome su Android ce l'ha di
// suo; Safari su iPhone e iPad no, e senza questo le strisce a barre le
// decodifica un vecchio motore in JavaScript che sui contatori sbaglia quasi
// sempre. Questo lo rimpiazza con lo stesso motore compilato, molto migliore.
import "barcode-detector/polyfill";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Camera, Keyboard, X } from "lucide-react";

/**
 * Legge un codice e restituisce quello che c'è scritto, senza interpretarlo.
 *
 * È diverso dal lettore dei QR dell'app, che riconosce solo i codici che
 * produciamo noi e porta alla pagina giusta. Qui il codice è stampato da un
 * altro — sul contatore c'è quello di Italgas — e a noi serve il testo grezzo:
 * chi lo riceve deciderà cosa farne.
 *
 * Legge sia i QR sia i codici a barre, perché sui contatori girano entrambi e
 * chi ha il pezzo in mano non deve chiedersi quale dei due sta inquadrando.
 *
 * E resta la tastiera: un'etichetta consumata o sporca di terra non si legge,
 * e senza un modo di scrivere la matricola a mano il lavoro si fermerebbe lì.
 */
const FORMATI = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

export default function ScannerCodice({ onLetto, onClose, titolo = "Inquadra il codice" }) {
  const [errore, setErrore] = useState(null);
  const [aMano, setAMano] = useState("");
  const letto = useRef(false);

  useEffect(() => {
    let scanner;
    let vivo = true;

    const config = {
      // Il riquadro è largo e basso: un codice a barre è una striscia, e
      // dentro un quadrato piccolo non ci sta mai per intero. Si adatta alla
      // larghezza dello schermo.
      fps: 15,
      qrbox: (w, h) => ({ width: Math.min(Math.round(w * 0.9), 420),
                          height: Math.min(Math.round(h * 0.5), 200) }),
    };
    const quandoLetto = (testo) => {
      if (letto.current) return;
      letto.current = true;
      scanner?.stop().catch(() => {});
      onLetto(testo);
    };

    // Ogni tentativo ha il suo lettore. Riusare lo stesso dopo un avvio
    // fallito non si può: resta "in transizione" e rifiuta di ripartire —
    // era l'errore che si vedeva sull'iPad al secondo tentativo.
    const avviaCon = async (fotocamera) => {
      const lettore = new Html5Qrcode("sgm-lettore-codice", {
        formatsToSupport: FORMATI,
        // I codici a barre li legge il lettore del browser, quando c'è; dove
        // manca lo mette il polyfill importato in testa a questo file.
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        verbose: false,
      });
      try {
        await lettore.start(fotocamera, config, quandoLetto, () => {});
        return lettore;
      } catch (e) {
        try { lettore.clear(); } catch (_) { /* era già fermo */ }
        throw e;
      }
    };

    const avvia = async () => {
      try {
        try {
          // La fotocamera posteriore alla risoluzione più alta che dà: una
          // striscia a barre si legge dalle sue righe sottili, e a bassa
          // risoluzione si fondono.
          scanner = await avviaCon(
            { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } });
        } catch (primo) {
          // Non tutti i browser accettano di scegliere la risoluzione: Safari
          // su iPad rifiuta. Si riprova chiedendo solo la fotocamera
          // posteriore, com'era prima, con un lettore nuovo.
          if (!vivo) return;
          scanner = await avviaCon({ facingMode: "environment" });
        }
        if (!vivo && scanner?.isScanning) scanner.stop().catch(() => {});
      } catch (e) {
        if (!vivo) return;
        const motivo = String(e?.message || e || "").toLowerCase();
        setErrore(motivo.includes("permission") || motivo.includes("denied") || motivo.includes("notallowed")
          ? "Il browser non ha il permesso di usare la fotocamera. Su iPad: Impostazioni › Safari › "
            + "Fotocamera › Consenti, poi ricarica la pagina. Intanto puoi scrivere la matricola a mano."
          : "La fotocamera non parte (" + (e?.message || e) + "). Prova a ricaricare la pagina; "
            + "intanto puoi scrivere la matricola a mano.");
      }
    };
    avvia();
    return () => {
      vivo = false;
      letto.current = true;
      if (scanner?.isScanning) scanner.stop().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scriviAMano = (e) => {
    e.preventDefault();
    if (!aMano.trim()) return;
    letto.current = true;
    onLetto(aMano.trim());
  };

  return (
    <div className="fixed inset-0 bg-slate-900/90 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-4 max-w-md w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold flex items-center gap-2">
            <Camera className="w-4 h-4 text-orange-500" /> {titolo}
          </h3>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="chiudi-scanner">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {errore ? (
          <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded flex gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />{errore}
          </div>
        ) : (
          <>
            <div id="sgm-lettore-codice" className="w-full rounded overflow-hidden" />
            <p className="text-xs text-slate-500 mt-2">
              Va bene sia il QR sia il codice a barre. Tieni il codice dentro il
              riquadro, dritto e senza ombra sopra; per le strisce a barre
              avvicinati finché riempiono il riquadro in larghezza.
            </p>
          </>
        )}

        <form onSubmit={scriviAMano} className="mt-3 border-t border-slate-200 pt-3">
          <label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
            <Keyboard className="w-3.5 h-3.5" /> Oppure scrivi la matricola
          </label>
          <div className="flex gap-2">
            <input value={aMano} onChange={(e) => setAMano(e.target.value)}
                   data-testid="codice-a-mano" autoComplete="off"
                   placeholder="Matricola stampata sul contatore"
                   className="flex-1 h-10 border border-slate-200 rounded px-3 text-sm" />
            <Button type="submit" disabled={!aMano.trim()}
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              Cerca
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
